import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { BehaviorSubject, takeWhile } from 'rxjs';
import { ConsultationFacade } from '../../../+store/consultation/consultation.facade';
import { IConsultation } from '@care-portals/api-interfaces';
import {
  ConsultationUtils,
  QuestionsCatalog,
  formTypes
} from '@care-portals/data';
import { klona } from 'klona';
import { AppFacade } from '../../../+store/app/app.facade';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzAffixModule } from 'ng-zorro-antd/affix';
import { FormBuilderModule } from '../../../modules/form-builder.module';
import { LookupComponent } from '@appgen-health-crm/ui';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';

@Component({
  selector: 'portals-emr-consultation-create',
  templateUrl: './create-consultation.component.html',
  styleUrls: ['./create-consultation.component.less'],
  standalone: true,
  providers: [],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzLayoutModule,
    NzSelectModule,
    NzButtonModule,
    NzAffixModule,
    FormBuilderModule,
    LookupComponent,
    NzCheckboxModule,
    NzIconModule,
    NzToolTipModule
  ]
})
export class CreateConsultationComponent implements OnInit, OnDestroy {
  isLoading = false;
  error;
  active = true;
  questions = [];
  internal = false;

  @Input() consultation?: IConsultation;
  @Input() customerId: string;

  answers = {};

  org: string;

  questionnairesCtrl = new FormControl();

  availableQnrs = [];

  $questionnaire = new BehaviorSubject(null);

  qsType = '';

  constructor(
    private facade: ConsultationFacade,
    private nzDrawerRef: NzDrawerRef,
    private appFacade: AppFacade
  ) {
    this.facade.creating$
      .pipe(takeWhile(() => this.active))
      .subscribe(res => (this.isLoading = res));
  }

  get qnrLabel() {
    return formTypes[this.consultation?.type]?.name;
  }
  ngOnInit() {
    this.appFacade.selectedLayout$
      .pipe(takeWhile(() => this.active))
      .subscribe(layout => {
        this.org = layout.organization;
        const questionnaireOpts = (layout?.questionnaires || []).map(
          qsKey => ({
            key: qsKey,
            name: formTypes[qsKey]?.name
          })
        );
        const keys = new Set(questionnaireOpts.map(q => q.key));
        const availableReqOpts = (
          layout?.availableRequirements || []
        ).reduce<{ key: string; name: string }[]>((opts, req) => {
          if (req?.type !== 'qnr' || !req?.id || keys.has(req.id)) {
            return opts;
          }
          opts.push({
            key: req.id,
            name: req.name || req.id
          });
          return opts;
        }, []);

        this.availableQnrs = [...questionnaireOpts, ...availableReqOpts];
      });

    this.questionnairesCtrl.valueChanges
      .pipe(takeWhile(() => this.active))
      .subscribe(qsName => {
        this.initQuestionnaire(qsName);
      });

    if (this.consultation) {
      if (this.consultation) {
        this.questionnairesCtrl.setValue(this.consultation.type);
      }
    }
  }

  initQuestionnaire(qsName: string) {
    this.qsType = qsName;

    this.$questionnaire.next(null);

    const qs = formTypes[qsName];

    if (!qs) {
      return;
    }

    setTimeout(() => {
      this.$questionnaire.next(qs);

      this.initForm();
    }, 500);
  }

  initForm() {
    this.clearAnswers();

    let questions = (this.$questionnaire.value?.questions || []).filter(
      question =>
        typeof question !== 'string' ||
        QuestionsCatalog.find(qModel => qModel.field === question).type !==
          'evaluated'
    );

    questions = ConsultationUtils.getAllQuestions(questions);

    this.questions = klona(questions);

    if (this.consultation?.answers) {
      this.setAnswers(klona(this.consultation.answers));
    }
  }

  clearAnswers() {
    for (const key of Object.keys(this.answers)) {
      delete this.answers[key];
    }
  }

  setAnswers(answers) {
    for (const key of Object.keys(answers)) {
      this.answers[key] = answers[key];
    }
  }

  ngOnDestroy(): void {
    this.active = false;
  }

  getScore(field: string) {
    const evaluateFunc = QuestionsCatalog.find(
      model => model.type === 'evaluated' && model.field === field
    ).answer;
    return evaluateFunc ? evaluateFunc(this.consultation.answers) : 0;
  }

  submit() {
    //filters out the empty answers
    this.answers = Object.fromEntries(
      Object.entries(this.answers).filter(
        ([key, value]) => Object.keys(value).length !== 0
      )
    );

    this.facade
      .saveConsultation({
        answers: this.answers,
        type: this.qsType,
        customerId: this.customerId,
        internal: this.internal
      })
      .then(consult => {
        this.closeDrawer();
      });
  }

  closeDrawer() {
    this.nzDrawerRef.close();
  }
}
